import React from 'react'
import { 
  Shield, 
  FileText, 
  AlertTriangle, 
  Scale, 
  Users, 
  Zap, 
  Lock, 
  Globe, 
  Mail,
  AlertCircle,
  ScrollText,
  Gavel
} from 'lucide-react'
import { cn } from '../lib/utils'

/**
 * Terms of Service Page Component
 * 
 * Displays the legal terms and conditions for using the CTO Pools platform.
 * Features:
 * - Comprehensive legal sections
 * - Cyberpunk styling consistent with the app
 * - Accessible structure with semantic HTML
 * - Prominent draft disclaimer
 */
export function TermsOfService(): JSX.Element {
  
  /**
   * Section component for consistent styling of ToS clauses
   */
  const Section = ({ 
    title, 
    icon: Icon, 
    children, 
    className 
  }: { 
    title: string
    icon?: React.ElementType
    children: React.ReactNode
    className?: string
  }) => (
    <section className={cn("mb-10 scroll-mt-24", className)} id={title.toLowerCase().replace(/\s+/g, '-')}>
      <div className="flex items-center space-x-3 mb-4">
        {Icon && (
          <div className="w-8 h-8 bg-cyber-cyan/10 rounded-cyber flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-cyber-cyan" aria-hidden="true" />
          </div>
        )}
        <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
      </div>
      <div className="text-text-secondary leading-relaxed space-y-4 pl-0 md:pl-11">
        {children}
      </div>
    </section>
  )

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyber-cyan/20 rounded-cyber mb-6 animate-pulse-slow">
            <ScrollText className="w-10 h-10 text-cyber-cyan" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Please read these terms carefully before using the CTO Pools platform.
          </p>
          <p className="text-sm text-text-muted mt-4">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Draft Disclaimer */}
        <div className="mb-12 glass-card border-l-4 border-l-status-warning border-y border-r border-status-warning/30 p-6 bg-status-warning/5">
          <div className="flex items-start space-x-4">
            <AlertTriangle className="w-6 h-6 text-status-warning flex-shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h3 className="text-lg font-bold text-status-warning mb-2">Notice</h3>
              <p className="text-text-secondary">
                This Terms of Service is subject to change.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="glass-card border border-dark-border/50 p-8 md:p-12">
          
          <Section title="1. Acceptance of Terms" icon={FileText}>
            <p>
              By accessing or using the CTO Pools platform ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not access or use the Platform.
            </p>
            <p>
              The Platform is a decentralized application (dApp) running on the Solana blockchain. Your use of the Platform signifies your acknowledgement and agreement that you are solely responsible for understanding the risks associated with cryptographic tokens and blockchain technology.
            </p>
          </Section>

          <Section title="2. Service Description" icon={Globe}>
            <p>
              CTO Pools provides a decentralized governance interface that allows users to:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>Create community funding pools associated with specific Solana tokens.</li>
              <li>Deposit SOL into these pools to support community initiatives.</li>
              <li>Create governance proposals (subject to holding requirements).</li>
              <li>Vote on proposals using a share-weighted mechanism.</li>
              <li>Participate in token burn mechanics via swap-and-burn functionality.</li>
            </ul>
            <p>
              The Platform functions as a non-custodial interface. We do not have custody or control over the tokens or funds you interact with on the Platform. All transactions occur directly on the Solana blockchain.
            </p>
          </Section>

          <Section title="3. Eligibility" icon={Users}>
            <p>
              To use the Platform, you represent and warrant that:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>You are at least 18 years of age or of legal age to form a binding contract under applicable law.</li>
              <li>You are not a resident, citizen, or agent of any country or region subject to comprehensive economic sanctions.</li>
              <li>Your use of the Platform does not violate any applicable laws or regulations in your jurisdiction.</li>
              <li>You have the technical knowledge necessary to use blockchain wallets and understand the risks involved.</li>
            </ul>
          </Section>

          <Section title="4. User Responsibilities" icon={Lock}>
            <p>
              <strong>Wallet Security:</strong> You are solely responsible for maintaining the security of your Solana wallet and private keys. We cannot access or recover your funds if you lose your private keys.
            </p>
            <p>
              <strong>Accurate Information:</strong> You agree to provide accurate information when creating proposals or interacting with the Platform.
            </p>
            <p>
              <strong>Compliance:</strong> You are responsible for ensuring your activities on the Platform comply with all applicable laws, including tax obligations and financial regulations.
            </p>
          </Section>

          <Section title="5. Platform Operations" icon={Zap}>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-text-primary mb-2">Pool Creation & Management</h3>
                <p>Users can create pools for any Solana token. Pools are governed by smart contracts and cannot be unilaterally altered by the Platform developers once deployed.</p>
              </div>
              
              <div>
                <h3 className="font-bold text-text-primary mb-2">Proposals & Voting</h3>
                <p>To create a proposal, a user must hold at least 1 SOL in the relevant pool for a minimum of 24 hours. Voting power is proportional to the user's share of the pool. Proposals are executed automatically via smart contract upon passing.</p>
              </div>

              <div>
                <h3 className="font-bold text-text-primary mb-2">Deposits & Withdrawals</h3>
                <p>Funds deposited into pools are locked based on active proposals. Users can withdraw their funds at any time, provided they are not locked in an active proposal they voted on, or subject to other smart contract constraints.</p>
              </div>

              <div>
                <h3 className="font-bold text-text-primary mb-2">Token Burns</h3>
                <p>The Platform includes mechanisms to swap SOL for tokens and burn them. This process is irreversible. Users acknowledge that burning tokens removes them permanently from circulation.</p>
              </div>
            </div>
          </Section>

          <Section title="6. Cryptocurrency Risks Disclaimer" icon={AlertCircle}>
            <p className="font-bold text-status-warning">
              Please read this section carefully.
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>
                <strong>Volatile Nature:</strong> The prices of cryptographic tokens are extremely volatile. You may lose all value in your tokens or deposited funds.
              </li>
              <li>
                <strong>Risk of Total Loss:</strong> You acknowledge and accept the risk of total loss of funds.
              </li>
              <li>
                <strong>No Guarantees:</strong> There are no guarantees of returns or profits. Participation in pools or governance does not guarantee any financial return.
              </li>
              <li>
                <strong>Speculative Nature:</strong> Cryptocurrency investments are highly speculative and involve a high degree of risk.
              </li>
              <li>
                <strong>User Responsibility:</strong> Users are solely responsible for their own investment decisions.
              </li>
              <li>
                <strong>Not Financial Advice:</strong> CTO Pools is not providing financial, investment, or legal advice. Information provided on the Platform is for informational purposes only. You should conduct your own research (DYOR).
              </li>
              <li>
                <strong>Smart Contract Risk:</strong> While we strive for security, smart contracts may contain bugs or vulnerabilities. We do not guarantee that the Platform is free from defects.
              </li>
              <li>
                <strong>Regulatory Uncertainty:</strong> Blockchain technology is subject to evolving regulations which may impact the Platform's operation.
              </li>
            </ul>
          </Section>

          <Section title="7. Third-Party Affiliation Disclaimer" icon={AlertTriangle}>
            <p>
              <strong>CTO Pools is a decentralized platform used to promote tokens, projects, and other businesses.</strong>
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>
                <strong>No Affiliation:</strong> CTO Pools is <strong>NOT</strong> affiliated with, endorsed by, or responsible for <strong>ANY</strong> token, project, or business promoted on the platform.
              </li>
              <li>
                <strong>Official Token:</strong> The <strong>ONLY</strong> token officially associated with CTO Pools is the official <strong>CTOP</strong> token (when launched).
              </li>
              <li>
                <strong>Due Diligence:</strong> Users should conduct their own due diligence (DYOR) before participating in any pool. We do not vet or endorse any specific pool or project.
              </li>
            </ul>
          </Section>

          <Section title="8. Prohibited Activities" icon={Shield}>
            <p>You agree not to engage in any of the following activities:</p>
            <ul className="list-disc list-outside ml-5 space-y-2">
              <li>Market manipulation, pump-and-dump schemes, or wash trading.</li>
              <li>Creating fraudulent proposals or pools intended to deceive users.</li>
              <li>Attempting to hack, exploit, or disrupt the Platform's infrastructure or smart contracts.</li>
              <li>Using the Platform for money laundering, terrorist financing, or other illegal activities.</li>
              <li>Interfering with other users' access to or use of the Platform.</li>
            </ul>
          </Section>

          <Section title="9. Intellectual Property" icon={Scale}>
            <p>
              The Platform's code is open source, but the "CTO Pools" brand, logo, and specific design elements are the intellectual property of the project maintainers. You may not use our branding in a way that suggests endorsement or affiliation without permission.
            </p>
          </Section>

          <Section title="10. Limitation of Liability" icon={AlertTriangle}>
            <p className="uppercase text-sm tracking-wide mb-2 text-text-muted">
              To the maximum extent permitted by law:
            </p>
            <p>
              In no event shall CTO Pools, its developers, contributors, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 mt-2">
              <li>Your access to or use of or inability to access or use the Platform;</li>
              <li>Any conduct or content of any third party on the Platform;</li>
              <li>Any content obtained from the Platform; and</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
            </ul>
          </Section>

          <Section title="11. Indemnification" icon={Shield}>
            <p>
              You agree to defend, indemnify, and hold harmless CTO Pools and its contributors from and against any claims, damages, obligations, losses, liabilities, costs, and expenses arising from: (a) your use of the Platform; (b) your violation of these Terms; or (c) your violation of any third-party right.
            </p>
          </Section>

          <Section title="12. Modifications to Terms" icon={FileText}>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by updating the "Last Updated" date at the top of this page. Your continued use of the Platform after any such change constitutes your acceptance of the new Terms.
            </p>
          </Section>

          <Section title="13. Governing Law" icon={Gavel}>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction Placeholder], without regard to its conflict of law provisions. Any disputes arising from these Terms or the Platform shall be resolved in the courts of [Jurisdiction Placeholder].
            </p>
          </Section>

          <Section title="14. Contact Information" icon={Mail}>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <a 
              href="mailto:inventcryptomain@gmail.com" 
              className="inline-flex items-center space-x-2 text-cyber-cyan hover:text-cyber-cyan/80 transition-colors mt-2"
            >
              <Mail className="w-4 h-4" />
              <span>inventcryptomain@gmail.com</span>
            </a>
          </Section>

        </div>
      </div>
    </div>
  )
}

export default TermsOfService
